import React, { useState, useEffect, useCallback } from 'react';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';

const ICONS = {
  plus:     'M12 4v16m8-8H4',
  edit:     'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  trash:    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  save:     'M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4',
  times:    'M6 18L18 6M6 6l12 12',
  question: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  list:     'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  check:    'M5 13l4 4L19 7',
  text:     'M4 6h16M4 12h16M4 18h7',
  yn:       'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  multi:    'M4 6h16M4 10h16M4 14h16M4 18h16',
};

const SvgIcon = ({ d, size = 14, color = 'currentColor', stroke = 2 }) => (
  <svg width={size} height={size} fill="none" stroke={color} viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={stroke} d={d} />
  </svg>
);

const inputBase = {
  width: '100%', padding: '9px 12px', borderRadius: '8px',
  border: '1px solid #e5e7eb', fontSize: '13px', color: '#374151',
  fontFamily: 'inherit', outline: 'none', backgroundColor: '#fff',
  boxSizing: 'border-box', transition: 'border-color .15s, box-shadow .15s',
};
const fh = {
  onFocus: e => { e.target.style.borderColor = '#2563eb'; e.target.style.boxShadow = '0 0 0 3px rgba(37,99,235,.1)'; },
  onBlur:  e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; },
};

const Label = ({ children }) => (
  <label style={{ fontSize: '11px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', display: 'block', marginBottom: '5px' }}>
    {children}
  </label>
);

const TIPO_PERGUNTA_META = {
  texto_livre:      { label: 'Texto Livre',      icon: ICONS.text,  bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe' },
  booleano:         { label: 'Sim / Não',         icon: ICONS.yn,    bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0' },
  multipla_escolha: { label: 'Múltipla Escolha', icon: ICONS.multi, bg: '#fdf4ff', color: '#9333ea', border: '#e9d5ff' },
};

const TipoBadge = ({ tipo }) => {
  const meta = TIPO_PERGUNTA_META[tipo] || TIPO_PERGUNTA_META.texto_livre;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '999px', backgroundColor: meta.bg, color: meta.color, border: `1px solid ${meta.border}` }}>
      <SvgIcon d={meta.icon} size={10} color={meta.color} />
      {meta.label}
    </span>
  );
};

/* ── MODAL ── */
const AnamneseModal = ({ show, onClose, onSave, currentQuestion, isSaving }) => {
  const [formData, setFormData] = useState({ tipo_ficha: 'Medico', pergunta: '', tipo_pergunta: 'texto_livre', opcoes: [] });
  const [newOption, setNewOption] = useState('');

  useEffect(() => {
    if (show) {
      if (currentQuestion) {
        setFormData({
          tipo_ficha:    currentQuestion.tipo_ficha,
          pergunta:      currentQuestion.pergunta,
          tipo_pergunta: currentQuestion.tipo_pergunta || 'texto_livre',
          opcoes:        Array.isArray(currentQuestion.opcoes) ? currentQuestion.opcoes : [],
        });
      } else {
        setFormData({ tipo_ficha: 'Medico', pergunta: '', tipo_pergunta: 'texto_livre', opcoes: [] });
      }
      setNewOption('');
    }
  }, [show, currentQuestion]);

  const handleChange = e => setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleAddOption = () => {
    if (newOption.trim()) {
      setFormData(prev => ({ ...prev, opcoes: [...prev.opcoes, newOption.trim()] }));
      setNewOption('');
    }
  };

  const handleOptionKeyDown = e => {
    if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); }
  };

  const handleRemoveOption = idx =>
    setFormData(prev => ({ ...prev, opcoes: prev.opcoes.filter((_, i) => i !== idx) }));

  if (!show) return null;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,.5)', backdropFilter: 'blur(3px)' }} />
      <div style={{ position: 'relative', backgroundColor: '#fff', borderRadius: '16px', width: '100%', maxWidth: '580px', boxShadow: '0 25px 60px rgba(0,0,0,.2)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SvgIcon d={ICONS.question} size={16} color="#fff" />
            </div>
            <span style={{ fontSize: '15px', fontWeight: '800', color: '#fff' }}>
              {currentQuestion ? 'Editar Pergunta' : 'Nova Pergunta'}
            </span>
          </div>
          <button
            type="button" onClick={onClose}
            style={{ width: '28px', height: '28px', borderRadius: '7px', border: 'none', backgroundColor: 'rgba(255,255,255,.15)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <SvgIcon d={ICONS.times} size={14} color="#fff" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '16px' }}>
            <div>
              <Label>Tipo de Ficha</Label>
              <select name="tipo_ficha" value={formData.tipo_ficha} onChange={handleChange} style={{ ...inputBase }} {...fh}>
                <option value="Medico">Médico</option>
              </select>
            </div>
            <div>
              <Label>Tipo de Resposta</Label>
              <select name="tipo_pergunta" value={formData.tipo_pergunta} onChange={handleChange} style={{ ...inputBase }} {...fh}>
                <option value="texto_livre">Texto Livre</option>
                <option value="booleano">Sim / Não</option>
                <option value="multipla_escolha">Múltipla Escolha</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <Label>Enunciado da Pergunta</Label>
            <textarea
              name="pergunta" value={formData.pergunta} onChange={handleChange}
              rows={3} style={{ ...inputBase, resize: 'vertical' }}
              placeholder="Digite a pergunta que será exibida na ficha de anamnese..."
              {...fh}
            />
          </div>

          {formData.tipo_pergunta === 'multipla_escolha' && (
            <div style={{ backgroundColor: '#fdf4ff', borderRadius: '10px', border: '1px solid #e9d5ff', padding: '14px' }}>
              <Label>Opções do Menu</Label>
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                <input
                  type="text" value={newOption}
                  onChange={e => setNewOption(e.target.value)}
                  onKeyDown={handleOptionKeyDown}
                  placeholder="Ex: Hipertenso — pressione Enter para adicionar"
                  style={{ ...inputBase, flex: 1 }}
                  {...fh}
                />
                <button
                  type="button" onClick={handleAddOption}
                  style={{ padding: '9px 16px', borderRadius: '8px', border: 'none', background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', flexShrink: 0 }}
                >
                  + Adicionar
                </button>
              </div>

              {formData.opcoes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '12px', color: '#a855f7', fontSize: '12px' }}>
                  Nenhuma opção adicionada ainda
                </div>
              ) : (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {formData.opcoes.map((opt, idx) => (
                    <span
                      key={idx}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', backgroundColor: '#fff', border: '1px solid #d8b4fe', fontSize: '12px', fontWeight: '600', color: '#7c3aed' }}
                    >
                      {opt}
                      <button
                        type="button" onClick={() => handleRemoveOption(idx)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center' }}
                      >
                        <SvgIcon d={ICONS.times} size={10} color="#dc2626" stroke={2.5} />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'flex-end', gap: '10px', backgroundColor: '#f8fafc' }}>
          <button
            type="button" onClick={onClose} disabled={isSaving}
            style={{ padding: '9px 18px', borderRadius: '9px', border: '1px solid #e5e7eb', backgroundColor: '#fff', color: '#374151', fontSize: '13px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
          >
            Cancelar
          </button>
          <button
            type="button" onClick={() => onSave(formData)} disabled={isSaving}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '9px 18px', borderRadius: '9px', border: 'none', background: isSaving ? '#e5e7eb' : 'linear-gradient(135deg,#1e3a5f,#2563eb)', color: isSaving ? '#9ca3af' : '#fff', fontSize: '13px', fontWeight: '600', cursor: isSaving ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
          >
            {isSaving ? (
              <>
                <Spinner as="span" animation="border" size="sm" style={{ width: '12px', height: '12px', borderWidth: '2px' }} />
                Salvando...
              </>
            ) : (
              <>
                <SvgIcon d={ICONS.save} size={13} color="#fff" />
                Salvar
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── COMPONENTE PRINCIPAL ── */
const AnamneseTab = () => {
  const { currentEntidade } = useEntidadeContext();
  const { showAlertModal }  = useAlertModal();

  const [questions, setQuestions]             = useState([]);
  const [isLoading, setIsLoading]             = useState(false);
  const [showModal, setShowModal]             = useState(false);
  const [isSaving, setIsSaving]               = useState(false);
  const [currentQuestion, setCurrentQuestion] = useState(null);

  const fetchQuestions = useCallback(async () => {
    if (!currentEntidade) return;
    setIsLoading(true);
    try {
      const response = await axios.get(`/api/panamneses?entidade_id=${currentEntidade.id}`);
      setQuestions(response.data || []);
    } catch {
      showAlertModal('Erro', 'Não foi possível carregar as perguntas de anamnese.', 'danger');
    } finally {
      setIsLoading(false);
    }
  }, [currentEntidade, showAlertModal]);

  useEffect(() => { fetchQuestions(); }, [fetchQuestions]);

  const handleOpenModal = (question = null) => {
    setCurrentQuestion(question);
    setShowModal(true);
  };

  const handleSave = async (formData) => {
    if (!formData.pergunta.trim()) {
      showAlertModal('Atenção', 'A pergunta não pode estar vazia.', 'warning');
      return;
    }
    if (formData.tipo_pergunta === 'multipla_escolha' && formData.opcoes.length === 0) {
      showAlertModal('Atenção', 'Múltipla escolha exige pelo menos uma opção.', 'warning');
      return;
    }
    setIsSaving(true);
    try {
      if (currentQuestion) {
        await axios.put(`/api/panamneses/${currentQuestion.id}`, formData);
        showAlertModal('Sucesso', 'Pergunta atualizada!', 'success');
      } else {
        await axios.post('/api/panamneses', { ...formData, entidade_id: currentEntidade.id });
        showAlertModal('Sucesso', 'Pergunta criada!', 'success');
      }
      setShowModal(false);
      fetchQuestions();
    } catch {
      showAlertModal('Erro', 'Falha ao salvar a pergunta.', 'danger');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta pergunta? Isso não afetará anamneses já preenchidas.')) return;
    try {
      await axios.delete(`/api/panamneses/${id}`);
      showAlertModal('Sucesso', 'Pergunta removida.', 'success');
      fetchQuestions();
    } catch {
      showAlertModal('Erro', 'Não foi possível excluir a pergunta.', 'danger');
    }
  };

  return (
    <div style={{ fontFamily: "'Inter','Segoe UI',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');`}</style>

      <AnamneseModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        currentQuestion={currentQuestion}
        isSaving={isSaving}
      />

      {/* HEADER */}
      <div style={{ background: 'linear-gradient(135deg,#1e3a5f,#2563eb)', borderRadius: '14px', padding: '20px 24px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
          <div style={{ width: '44px', height: '44px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <SvgIcon d={ICONS.list} size={22} color="#fff" stroke={1.8} />
          </div>
          <div>
            <div style={{ fontSize: '16px', fontWeight: '800', color: '#fff' }}>Perguntas de Anamnese</div>
            <div style={{ fontSize: '12px', color: 'rgba(255,255,255,.7)' }}>
              {questions.length} {questions.length === 1 ? 'pergunta cadastrada' : 'perguntas cadastradas'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => handleOpenModal()}
          style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 18px', borderRadius: '10px', border: 'none', backgroundColor: 'rgba(255,255,255,.15)', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: 'inherit', backdropFilter: 'blur(4px)' }}
        >
          <SvgIcon d={ICONS.plus} size={14} color="#fff" stroke={2.5} />
          Nova Pergunta
        </button>
      </div>

      {/* CONTEÚDO */}
      <div style={{ backgroundColor: '#fff', borderRadius: '14px', border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        {isLoading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '48px 0', gap: '12px' }}>
            <Spinner animation="border" variant="primary" style={{ width: '28px', height: '28px' }} />
            <span style={{ fontSize: '13px', color: '#6b7280' }}>Carregando perguntas...</span>
          </div>
        ) : questions.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '56px 20px', gap: '12px' }}>
            <div style={{ width: '56px', height: '56px', borderRadius: '14px', backgroundColor: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <SvgIcon d={ICONS.question} size={28} color="#94a3b8" stroke={1.5} />
            </div>
            <div style={{ fontSize: '14px', fontWeight: '700', color: '#374151' }}>Nenhuma pergunta cadastrada</div>
            <div style={{ fontSize: '12px', color: '#9ca3af' }}>Clique em "Nova Pergunta" para começar</div>
          </div>
        ) : (
          <>
            {/* Cabeçalho da tabela */}
            <div style={{ display: 'grid', gridTemplateColumns: '110px 160px 1fr 90px', padding: '10px 20px', backgroundColor: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
              {['Ficha', 'Tipo', 'Pergunta', 'Ações'].map((h, i) => (
                <div key={h} style={{ fontSize: '10px', fontWeight: '700', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '.5px', textAlign: i === 3 ? 'center' : 'left' }}>
                  {h}
                </div>
              ))}
            </div>

            {/* Linhas */}
            {questions.map((q, idx) => (
              <div
                key={q.id}
                style={{
                  display: 'grid', gridTemplateColumns: '110px 160px 1fr 90px',
                  padding: '14px 20px', alignItems: 'center',
                  borderBottom: idx < questions.length - 1 ? '1px solid #f1f5f9' : 'none',
                  backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                  transition: 'background .15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#f0f7ff'; }}
                onMouseLeave={e => { e.currentTarget.style.backgroundColor = idx % 2 === 0 ? '#fff' : '#fafafa'; }}
              >
                {/* Ficha */}
                <div>
                  <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 9px', borderRadius: '999px', backgroundColor: '#eff6ff', color: '#2563eb', border: '1px solid #bfdbfe' }}>
                    {q.tipo_ficha}
                  </span>
                </div>

                {/* Tipo */}
                <div>
                  <TipoBadge tipo={q.tipo_pergunta} />
                </div>

                {/* Pergunta */}
                <div style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', paddingRight: '16px' }}>
                  {q.pergunta}
                  {q.tipo_pergunta === 'multipla_escolha' && Array.isArray(q.opcoes) && q.opcoes.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' }}>
                      {q.opcoes.slice(0, 4).map((opt, i) => (
                        <span key={i} style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '999px', backgroundColor: '#fdf4ff', color: '#9333ea', border: '1px solid #e9d5ff' }}>
                          {opt}
                        </span>
                      ))}
                      {q.opcoes.length > 4 && (
                        <span style={{ fontSize: '10px', padding: '1px 7px', borderRadius: '999px', backgroundColor: '#f3f4f6', color: '#6b7280' }}>
                          +{q.opcoes.length - 4} mais
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Ações */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                  <button
                    type="button"
                    onClick={() => handleOpenModal(q)}
                    title="Editar"
                    style={{ width: '30px', height: '30px', borderRadius: '7px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#2563eb'; e.currentTarget.style.borderColor = '#2563eb'; e.currentTarget.querySelector('svg').style.stroke = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#eff6ff'; e.currentTarget.style.borderColor = '#bfdbfe'; e.currentTarget.querySelector('svg').style.stroke = '#2563eb'; }}
                  >
                    <SvgIcon d={ICONS.edit} size={13} color="#2563eb" />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(q.id)}
                    title="Excluir"
                    style={{ width: '30px', height: '30px', borderRadius: '7px', border: '1px solid #fecaca', backgroundColor: '#fef2f2', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#dc2626'; e.currentTarget.style.borderColor = '#dc2626'; e.currentTarget.querySelector('svg').style.stroke = '#fff'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#fef2f2'; e.currentTarget.style.borderColor = '#fecaca'; e.currentTarget.querySelector('svg').style.stroke = '#dc2626'; }}
                  >
                    <SvgIcon d={ICONS.trash} size={13} color="#dc2626" />
                  </button>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default AnamneseTab;
